/** Editorial content – Skills Up Certifications page */

export const certHero = {
  badge: 'SkillsUp AI — Applied Credentials',
  titleLead: 'Credentials that',
  titleAccent: 'actually travel',
  description:
    'Every SkillsUp AI deployment program is built around measurable execution outcomes — not completion badges. Our applied credentials are recognized at the Moroccan, African, and international level, giving your AI fluency institutional weight wherever you operate.',
} as const

export const certIntro = {
  kicker: 'SkillsUp AI Quality Architecture',
  headline: 'Intelligence deployment, certified at every level',
  sub:
    'Our credentials are not paperwork. They are a quality architecture — vetted by Moroccan institutions, international networks, and European training standards. When you complete a SkillsUp AI program, you earn layered recognition that signals execution capability, not just participation.',
} as const

export const certPlanStrip = {
  title: 'Built for a career that goes further',
  subtitle: 'Across Africa and beyond',
  body:
    'Benefit from layered recognition: Moroccan institutional standards, international quality partnerships, and European training rigor — all in one program.',
} as const

export type CertificationPillar = {
  id: string
  icon: string
  label: string
  title: string
  body: string
  advantageTitle: string
  advantageBody?: string
  bullets?: readonly string[]
}

export const certificationPillars: readonly CertificationPillar[] = [
  {
    id: 'maroc',
    icon: 'flag',
    label: 'Moroccan Certification',
    title: 'Rooted in Morocco, Built for Africa',
    body: '',
    advantageTitle: 'Why it matters for you',
    advantageBody: undefined,
  },
  {
    id: 'mondial',
    icon: 'public',
    label: 'Global Certification',
    title: 'Internationally Recognized Standards',
    body: '',
    advantageTitle: 'Why it matters for you',
    advantageBody: undefined,
  },
  {
    id: 'europe',
    icon: 'verified',
    label: 'European Quality Standard',
    title: 'Qualiopi — European Quality Mark',
    body: '',
    advantageTitle: 'Why it matters for you',
    advantageBody: undefined,
    bullets: [
      'Official recognition of training quality — pedagogy, mentorship, and instructor credentials.',
      'Stronger market visibility: a Qualiopi-aligned certificate reassures employers about your seriousness.',
      'Possible access to public or mutual funding when the program is eligible.',
      'A mark of quality for international career moves and structured upskilling paths.',
      'European-standard traceability — recognized beyond borders.',
    ] as const,
  },
] as const

export const qaheSection = {
  kicker: 'Skills Up Accreditation',
  title: 'QAHE — Quality Assurance in Higher Education',
  intro:
    'The QAHE (International Association for Quality Assurance in Higher Education) sets the benchmark for learning institutions worldwide. Skills Up programs are delivered within a QAHE-accredited framework — holding our training to the same standards as leading global institutions.',
  qaheIs: [
    'An independent accreditation body recognized across multiple countries, evaluating institutions on pedagogy, governance, and learning outcomes.',
    'A quality seal specifically designed for online and distance learning — the model Skills Up is built on.',
    'A global network spanning Africa, Asia, Europe, and the Middle East — giving our graduates international visibility.',
  ] as const,
  positioning:
    'Operating within a QAHE-accredited framework means Skills Up programs meet the same standards as top online institutions globally. It positions your certificate above the noise of unverified online courses — giving you a credential that stands up to scrutiny.',
  advantageTitle: 'What this means for you',
  advantages: [
    {
      title: 'International academic recognition',
      text: 'Your certificate carries weight beyond Morocco — recognized by partner institutions and respected by international recruiters.',
    },
    {
      title: 'Better employability',
      text: 'Hiring managers increasingly scrutinize where credentials come from. A QAHE-aligned program signals rigor, professionalism, and verified outcomes.',
    },
    {
      title: 'Guaranteed pedagogical quality',
      text: 'Continuous evaluation of content, methods, and instructors — so every program stays current, relevant, and professionally delivered.',
    },
    {
      title: 'International mobility',
      text: 'Open doors to international partnerships, recognition of prior learning, and opportunities that require verified credentials.',
    },
    {
      title: 'Stand out in a crowded market',
      text: 'In a world full of online certificates, a QAHE-aligned program marks Skills Up as a credible, transparent, and results-driven platform.',
    },
  ] as const,
  closing:
    'This accreditation is not a logo — it is a promise to every Skills Up learner that their time, money, and ambition are invested in something real.',
} as const